import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Paper,
  Divider,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FilterListIcon from "@mui/icons-material/FilterList";
import DescriptionIcon from "@mui/icons-material/Description";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import { useSelector } from "react-redux";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";
import { convertMessageToHtml } from "../../utilities/helpers";
import { MESSAGE_STYLES } from "../../utilities/constants";

const CRLSummary = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Company picker state
  const [crlLetters, setCrlLetters] = useState([]);
  const [crlPage, setCrlPage] = useState(1);
  const [crlTotalPages, setCrlTotalPages] = useState(1);
  const [crlTotalCount, setCrlTotalCount] = useState(0);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [isLoadingCrl, setIsLoadingCrl] = useState(false);
  const [openCompanyPicker, setOpenCompanyPicker] = useState(false);
  const [selectedCompanyData, setSelectedCompanyData] = useState(null);
  const [isLoadingCompanyDetails, setIsLoadingCompanyDetails] = useState(false);

  // Extractions list state (Tab 1)
  const [allCrlList, setAllCrlList] = useState([]);
  const [fdaCenterFilter, setFdaCenterFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [availableCenters, setAvailableCenters] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [filteredCrl, setFilteredCrl] = useState([]);
  const [filteredPage, setFilteredPage] = useState(1);
  const [filteredTotalPages, setFilteredTotalPages] = useState(1);
  const [filteredTotalCount, setFilteredTotalCount] = useState(0);
  const [isLoadingList, setIsLoadingList] = useState(false);

  // Detail dialog
  const [selectedCrl, setSelectedCrl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const idToken = useSelector((state) => state.token);
  const requestHeader = { headers: { Authorization: idToken } };

  useEffect(() => {
    if (activeTab === 0) {
      fetchCrlLetters(crlPage === 1 && !companySearchTerm);
    }
    // eslint-disable-next-line
  }, [crlPage, companySearchTerm, activeTab]);

  useEffect(() => {
    if (activeTab === 1 && allCrlList.length === 0) {
      fetchAllCrlForFilter();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 1 && allCrlList.length > 0) {
      applyFiltersToData(allCrlList);
    }
    // eslint-disable-next-line
  }, [fdaCenterFilter, yearFilter, filteredPage, allCrlList]);

  const fetchCrlLetters = async (autoSelectFirst = false) => {
    try {
      setIsLoadingCrl(true);
      setErrorMessage(null);
      const params = new URLSearchParams({ page: crlPage.toString(), page_size: "20" });
      if (companySearchTerm) params.append("company_name", companySearchTerm);
      const response = await ieqipCoreServiceApi.get(`/api/crl/extractions?${params}`, requestHeader);
      const data = response.data;
      setCrlLetters(data.crl_letters || []);
      setCrlTotalPages(data.total_pages || 1);
      setCrlTotalCount(data.total_count || 0);
      if (autoSelectFirst && !selectedCompanyData && data.crl_letters?.length > 0) {
        handleSelectCompany(data.crl_letters[0]);
      }
    } catch (error) {
      setErrorMessage(`Failed to fetch CRL letters: ${error.message}`);
    } finally {
      setIsLoadingCrl(false);
    }
  };

  const fetchAllCrlForFilter = async () => {
    try {
      setIsLoadingList(true);
      setErrorMessage(null);
      const response = await ieqipCoreServiceApi.get("/api/crl/extractions?page=1&page_size=100", requestHeader);
      const data = response.data;
      const items = data.crl_letters || [];
      setAllCrlList(items);
      const centers = [...new Set(items.map((c) => c.fda_center).filter(Boolean))].sort();
      const years = [...new Set(items.map((c) => {
        if (c.date_issued) return new Date(c.date_issued).getFullYear().toString();
        return null;
      }).filter(Boolean))].sort().reverse();
      setAvailableCenters(centers);
      setAvailableYears(years);
      applyFiltersToData(items);
    } catch (error) {
      setErrorMessage(`Failed to fetch CRL list: ${error.message}`);
    } finally {
      setIsLoadingList(false);
    }
  };

  const applyFiltersToData = (data) => {
    let filtered = [...data];
    if (fdaCenterFilter) filtered = filtered.filter((c) => c.fda_center === fdaCenterFilter);
    if (yearFilter) {
      filtered = filtered.filter((c) => {
        if (c.date_issued) return new Date(c.date_issued).getFullYear().toString() === yearFilter;
        return false;
      });
    }
    const pageSize = 20;
    const totalCount = filtered.length;
    const totalPages = Math.ceil(totalCount / pageSize) || 1;
    const startIndex = (filteredPage - 1) * pageSize;
    setFilteredCrl(filtered.slice(startIndex, startIndex + pageSize));
    setFilteredTotalPages(totalPages);
    setFilteredTotalCount(totalCount);
  };

  const handleSelectCompany = async (crl) => {
    try {
      setIsLoadingCompanyDetails(true);
      setErrorMessage(null);
      const params = new URLSearchParams({ page: "1", page_size: "100", company_name: crl.company_name });
      const response = await ieqipCoreServiceApi.get(`/api/crl/extractions?${params}`, requestHeader);
      const data = response.data;
      setSelectedCompanyData({
        name: crl.company_name,
        address: crl.company_street_address,
        city: crl.company_city,
        state: crl.company_state,
        zipCode: crl.company_zip_code,
        country: crl.company_country,
        crlLetters: data.crl_letters || [],
      });
      setOpenCompanyPicker(false);
    } catch (error) {
      setErrorMessage(`Failed to fetch company details: ${error.message}`);
    } finally {
      setIsLoadingCompanyDetails(false);
    }
  };

  const getCompanyStats = () => {
    if (!selectedCompanyData) return { totalLetters: 0, totalDeficiencies: 0, totalProducts: 0 };
    const letters = selectedCompanyData.crlLetters || [];
    return {
      totalLetters: letters.length,
      totalDeficiencies: letters.reduce((s, c) => s + (c.deficiency_count || 0), 0),
      totalProducts: letters.reduce((s, c) => s + (c.product_count || 0), 0),
    };
  };

  const getDateDetails = () => {
    if (!selectedCompanyData) return { earliest: "N/A", latest: "N/A" };
    const dates = (selectedCompanyData.crlLetters || []).map((c) => c.date_issued).filter(Boolean).sort();
    if (!dates.length) return { earliest: "N/A", latest: "N/A" };
    return { earliest: dates[0], latest: dates[dates.length - 1] };
  };

  const getFdaCenters = () => {
    if (!selectedCompanyData) return [];
    return [...new Set((selectedCompanyData.crlLetters || []).map((c) => c.fda_center).filter(Boolean))];
  };

  const getAllDeficiencies = () => {
    if (!selectedCompanyData) return [];
    const result = [];
    (selectedCompanyData.crlLetters || []).forEach((crl) => {
      (crl.deficiencies || []).forEach((d) => {
        result.push({ ...d, letterType: crl.letter_type, dateIssued: crl.date_issued, proprietary_name: crl.proprietary_name });
      });
    });
    return result;
  };

  const getLetterStatusColor = (status) => {
    switch (status) {
      case "approved": return "success";
      case "tentatively_approved": return "info";
      case "pending_response": return "warning";
      case "withdrawn": return "error";
      default: return "default";
    }
  };

  const getLetterStatusLabel = (status) => {
    switch (status) {
      case "approved": return "APPROVED";
      case "tentatively_approved": return "TENTATIVE";
      case "pending_response": return "PENDING";
      case "withdrawn": return "WITHDRAWN";
      default: return (status || "N/A").toUpperCase();
    }
  };

  const stats = getCompanyStats();
  const dateDetails = getDateDetails();
  const fdaCenters = getFdaCenters();
  const allDeficiencies = getAllDeficiencies();

  return (
    <Box m="20px">
      <Typography variant="h2" fontWeight="bold" color="#1976d2" mb={2}>
        CRL Analysis
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab icon={<BusinessIcon />} iconPosition="start" label="Company Report" />
          <Tab icon={<DescriptionIcon />} iconPosition="start" label="CRL Extractions" />
        </Tabs>
      </Box>

      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

      {/* Tab 0: Company Report */}
      <Box role="tabpanel" hidden={activeTab !== 0}>
        {activeTab === 0 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h3" fontWeight="bold" color="#1976d2">
                Company CRL Report
              </Typography>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<BusinessIcon />}
                  onClick={() => setOpenCompanyPicker(true)}
                  sx={{ minWidth: 200 }}
                >
                  {selectedCompanyData ? selectedCompanyData.name : "Select Company"}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchCrlLetters()}
                  disabled={isLoadingCrl}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            {isLoadingCompanyDetails && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
              </Box>
            )}

            {selectedCompanyData && !isLoadingCompanyDetails && (
              <>
                <Typography variant="h3" fontWeight="bold" color="#1976d2" mb={3} textAlign="center">
                  {selectedCompanyData.name} - CRL Summary
                </Typography>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, border: "2px solid #1976d2", borderRadius: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <LocationOnIcon color="primary" />
                        <Typography variant="h6" color="primary" fontWeight="bold">Company Address:</Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="bold">{selectedCompanyData.address || "N/A"}</Typography>
                      <Typography variant="body2">
                        {[selectedCompanyData.city, selectedCompanyData.state, selectedCompanyData.zipCode].filter(Boolean).join(", ")}
                      </Typography>
                      <Typography variant="body2">{selectedCompanyData.country}</Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, border: "2px solid #1976d2", borderRadius: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <MedicalServicesIcon color="primary" />
                        <Typography variant="h6" color="primary" fontWeight="bold">Products Reviewed:</Typography>
                      </Box>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {[...new Set((selectedCompanyData.crlLetters || []).map((c) => c.proprietary_name).filter(Boolean))].map((p, i) => (
                          <Chip key={i} label={p} size="small" color="primary" variant="outlined" />
                        ))}
                        {!(selectedCompanyData.crlLetters || []).some((c) => c.proprietary_name) && (
                          <Typography variant="body1">N/A</Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, border: "2px solid #1976d2", borderRadius: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <BusinessIcon color="primary" />
                        <Typography variant="h6" color="primary" fontWeight="bold">FDA Centers:</Typography>
                      </Box>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {fdaCenters.length > 0
                          ? fdaCenters.map((c, i) => <Chip key={i} label={c} size="small" color="primary" variant="outlined" />)
                          : <Typography variant="body1">N/A</Typography>}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Stats */}
                <Typography variant="h4" fontWeight="bold" color="#1976d2" textAlign="center" mb={3}>
                  Overview Statistics
                </Typography>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {[
                    { label: "Total CRL Letters", value: stats.totalLetters },
                    { label: "Total Deficiencies", value: stats.totalDeficiencies },
                    { label: "Products Reviewed", value: stats.totalProducts },
                  ].map((stat, i) => (
                    <Grid item xs={12} md={4} key={i}>
                      <Card sx={{ background: "linear-gradient(135deg, #5472d3 0%, #3f51b5 100%)", color: "white", height: 150, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <CardContent sx={{ textAlign: "center" }}>
                          <Typography variant="h1" fontWeight="bold" mb={1}>{stat.value}</Typography>
                          <Typography variant="h6" fontWeight="500">{stat.label}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Timeline */}
                <Typography variant="h4" fontWeight="bold" color="#1976d2" textAlign="center" mb={3}>Timeline</Typography>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {[
                    { label: "Earliest CRL Letter:", value: dateDetails.earliest },
                    { label: "Latest CRL Letter:", value: dateDetails.latest },
                  ].map((item, i) => (
                    <Grid item xs={12} md={6} key={i}>
                      <Paper sx={{ p: 2, border: "2px solid #1976d2", borderRadius: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <CalendarTodayIcon color="primary" />
                          <Typography variant="h6" color="primary" fontWeight="bold">{item.label}</Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="bold">{item.value}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {/* CRL Letters Table */}
                <Typography variant="h4" fontWeight="bold" color="#1976d2" textAlign="center" mb={3}>
                  {selectedCompanyData.name}'s CRL Letters
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 4 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#1976d2" }}>
                      <TableRow>
                        {["Application #", "Date Issued", "Effective Date", "FDA Center", "Letter Type", "Deficiencies", "Status", "Actions"].map((h) => (
                          <TableCell key={h} sx={{ color: "white", fontWeight: "bold" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(selectedCompanyData.crlLetters || [])
                        .sort((a, b) => new Date(b.date_issued) - new Date(a.date_issued))
                        .map((crl, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              {(crl.application_numbers || []).map((a) => a.number || a).join(", ") || "N/A"}
                            </TableCell>
                            <TableCell>{crl.date_issued || "N/A"}</TableCell>
                            <TableCell>{crl.effective_date || "N/A"}</TableCell>
                            <TableCell>{crl.fda_center || "N/A"}</TableCell>
                            <TableCell sx={{ maxWidth: 200 }}>
                              <Typography variant="body2" noWrap title={crl.letter_type}>{crl.letter_type || "N/A"}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={crl.deficiency_count || 0} size="small" color={crl.deficiency_count > 0 ? "error" : "success"} />
                            </TableCell>
                            <TableCell>
                              <Chip label={getLetterStatusLabel(crl.letter_status)} size="small" color={getLetterStatusColor(crl.letter_status)} />
                            </TableCell>
                            <TableCell>
                              <IconButton color="primary" onClick={() => { setSelectedCrl(crl); setOpenDialog(true); }} size="small">
                                <VisibilityIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Deficiencies Summary */}
                {allDeficiencies.length > 0 && (
                  <>
                    <Typography variant="h4" fontWeight="bold" color="#1976d2" textAlign="center" mb={3}>
                      All Deficiencies Summary
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead sx={{ backgroundColor: "#1976d2" }}>
                          <TableRow>
                            {["Product", "Date", "Category", "Required Action"].map((h) => (
                              <TableCell key={h} sx={{ color: "white", fontWeight: "bold" }}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {allDeficiencies.slice(0, 20).map((d, i) => (
                            <TableRow key={i}>
                              <TableCell>{d.proprietary_name || "N/A"}</TableCell>
                              <TableCell>{d.dateIssued || "N/A"}</TableCell>
                              <TableCell>
                                <Chip label={d.category || "N/A"} size="small" variant="outlined" color="error" />
                              </TableCell>
                              <TableCell sx={{ maxWidth: 300 }}>
                                <Typography variant="body2" noWrap title={d.required_action}>{d.required_action || "N/A"}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Box>

      {/* Tab 1: CRL Extractions List */}
      <Box role="tabpanel" hidden={activeTab !== 1}>
        {activeTab === 1 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h3" fontWeight="bold" color="#1976d2">CRL Extractions</Typography>
              <Button variant="outlined" color="secondary" startIcon={<RefreshIcon />} onClick={() => { setAllCrlList([]); fetchAllCrlForFilter(); }} disabled={isLoadingList}>
                Refresh
              </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 3 }}>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <FilterListIcon color="primary" />
                <Typography variant="h6" color="primary" fontWeight="bold">Filters:</Typography>
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>FDA Center</InputLabel>
                  <Select value={fdaCenterFilter} label="FDA Center" onChange={(e) => { setFdaCenterFilter(e.target.value); setFilteredPage(1); }}>
                    <MenuItem value="">All Centers</MenuItem>
                    {availableCenters.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 130 }}>
                  <InputLabel>Year</InputLabel>
                  <Select value={yearFilter} label="Year" onChange={(e) => { setYearFilter(e.target.value); setFilteredPage(1); }}>
                    <MenuItem value="">All Years</MenuItem>
                    {availableYears.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
                {(fdaCenterFilter || yearFilter) && (
                  <Button variant="text" color="secondary" onClick={() => { setFdaCenterFilter(""); setYearFilter(""); setFilteredPage(1); }}>
                    Clear Filters
                  </Button>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" color="textSecondary">{filteredTotalCount} total extractions</Typography>
              </Box>
            </Paper>

            {isLoadingList && (
              <Box display="flex" justifyContent="center" minHeight="300px" alignItems="center"><CircularProgress /></Box>
            )}

            {!isLoadingList && (
              <>
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#1976d2" }}>
                      <TableRow>
                        {["Company", "Product", "App #", "FDA Center", "Date Issued", "Deficiencies", "Status", "Actions"].map((h) => (
                          <TableCell key={h} sx={{ color: "white", fontWeight: "bold" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCrl.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body1" color="textSecondary" py={4}>No CRL extractions found</Typography>
                          </TableCell>
                        </TableRow>
                      ) : filteredCrl.map((crl, idx) => (
                        <TableRow key={crl.id || idx}>
                          <TableCell><Typography variant="body2" fontWeight="bold">{crl.company_name || "N/A"}</Typography></TableCell>
                          <TableCell>{crl.proprietary_name || "N/A"}</TableCell>
                          <TableCell>{(crl.application_numbers || []).map((a) => a.number || a).join(", ") || "N/A"}</TableCell>
                          <TableCell>{crl.fda_center || "N/A"}</TableCell>
                          <TableCell>{crl.date_issued || "N/A"}</TableCell>
                          <TableCell>
                            <Chip label={crl.deficiency_count || 0} size="small" color={crl.deficiency_count > 0 ? "error" : "success"} />
                          </TableCell>
                          <TableCell>
                            <Chip label={(crl.extraction_status || "N/A").toUpperCase()} size="small"
                              color={crl.extraction_status === "completed" ? "success" : crl.extraction_status === "pending" ? "warning" : "error"} />
                          </TableCell>
                          <TableCell>
                            <IconButton color="primary" onClick={() => { setSelectedCrl(crl); setOpenDialog(true); }} size="small">
                              <VisibilityIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {filteredTotalPages > 1 && (
                  <Box display="flex" justifyContent="center" mb={3}>
                    <Pagination count={filteredTotalPages} page={filteredPage} onChange={(e, v) => setFilteredPage(v)} color="primary" showFirstButton showLastButton />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>

      {/* Company Picker Dialog */}
      <Dialog open={openCompanyPicker} onClose={() => setOpenCompanyPicker(false)} maxWidth="md" fullWidth>
        <DialogTitle><Typography variant="h5" fontWeight="bold">Select a Company</Typography></DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            placeholder="Search company by name..."
            value={companySearchTerm}
            onChange={(e) => { setCompanySearchTerm(e.target.value); setCrlPage(1); }}
            sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          {!isLoadingCrl && crlTotalCount > 0 && (
            <Typography variant="body2" color="textSecondary" mb={2}>
              Showing {crlLetters.length} of {crlTotalCount} CRL letters
              {crlTotalPages > 1 && ` (Page ${crlPage} of ${crlTotalPages})`}
            </Typography>
          )}
          {isLoadingCrl && <Box display="flex" justifyContent="center" minHeight="200px" alignItems="center"><CircularProgress /></Box>}
          {!isLoadingCrl && crlLetters.length === 0 && (
            <Box textAlign="center" py={4}><Typography color="textSecondary">No CRL letters found</Typography></Box>
          )}
          {!isLoadingCrl && crlLetters.length > 0 && (
            <List>
              {crlLetters.map((crl, idx) => (
                <React.Fragment key={crl.id || idx}>
                  <ListItem
                    sx={{ cursor: "pointer", borderRadius: 1, mb: 1, "&:hover": { backgroundColor: "#e3f2fd" } }}
                    onClick={() => handleSelectCompany(crl)}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight="bold">{crl.company_name || "Unknown Company"}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {crl.proprietary_name} {crl.date_issued ? `— ${crl.date_issued}` : ""} {crl.fda_center ? `| ${crl.fda_center}` : ""}
                      </Typography>
                    </Box>
                  </ListItem>
                  {idx < crlLetters.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
          {crlTotalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination count={crlTotalPages} page={crlPage} onChange={(e, v) => setCrlPage(v)} color="primary" size="small" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCompanyPicker(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* CRL Detail Dialog */}
      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setSelectedCrl(null); }} maxWidth="lg" fullWidth>
        {selectedCrl && (
          <>
            <DialogTitle>
              <Typography variant="h5" fontWeight="bold">CRL Details — {selectedCrl.company_name}</Typography>
              <Typography variant="body2" color="textSecondary">{selectedCrl.original_filename}</Typography>
            </DialogTitle>
            <DialogContent dividers>
              {selectedCrl.error_message && (
                <Alert severity="error" sx={{ mb: 2 }}>{selectedCrl.error_message}</Alert>
              )}

              <Grid container spacing={3}>
                {/* Letter Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight="bold" color="primary" mb={1}>Letter Information</Typography>
                  <Paper sx={{ p: 2 }}>
                    {[
                      { label: "Letter Type", value: selectedCrl.letter_type },
                      { label: "Application Type", value: selectedCrl.application_type },
                      { label: "Application Numbers", value: (selectedCrl.application_numbers || []).map((a) => `${a.number || a}${a.supplement ? ` (${a.supplement})` : ""}`).join(", ") },
                      { label: "FDA Center", value: selectedCrl.fda_center },
                      { label: "Reference ID", value: selectedCrl.reference_id },
                      { label: "Status", value: <Chip label={getLetterStatusLabel(selectedCrl.letter_status)} size="small" color={getLetterStatusColor(selectedCrl.letter_status)} /> },
                    ].map(({ label, value }) => (
                      <Box key={label} display="flex" gap={1} mb={0.5}>
                        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160 }}>{label}:</Typography>
                        <Typography variant="body2">{value || "N/A"}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>

                {/* Dates */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight="bold" color="primary" mb={1}>Key Dates</Typography>
                  <Paper sx={{ p: 2 }}>
                    {[
                      { label: "Date Issued", value: selectedCrl.date_issued },
                      { label: "Effective Date", value: selectedCrl.effective_date },
                      { label: "Response Due", value: selectedCrl.response_due_date },
                    ].map(({ label, value }) => (
                      <Box key={label} display="flex" gap={1} mb={0.5}>
                        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160 }}>{label}:</Typography>
                        <Typography variant="body2">{value || "N/A"}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>

                {/* Company Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight="bold" color="primary" mb={1}>Applicant Information</Typography>
                  <Paper sx={{ p: 2 }}>
                    {[
                      { label: "Company", value: selectedCrl.company_name },
                      { label: "US Agent", value: selectedCrl.us_agent },
                      { label: "Recipient", value: selectedCrl.recipient_name },
                      { label: "Title", value: selectedCrl.recipient_title },
                      { label: "Address", value: [selectedCrl.company_street_address, selectedCrl.company_city, selectedCrl.company_state, selectedCrl.company_zip_code, selectedCrl.company_country].filter(Boolean).join(", ") },
                    ].map(({ label, value }) => (
                      <Box key={label} display="flex" gap={1} mb={0.5}>
                        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160 }}>{label}:</Typography>
                        <Typography variant="body2">{value || "N/A"}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>

                {/* Product Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight="bold" color="primary" mb={1}>Product Information</Typography>
                  <Paper sx={{ p: 2 }}>
                    {[
                      { label: "Proprietary Name", value: selectedCrl.proprietary_name },
                      { label: "Established Name", value: selectedCrl.established_name },
                      { label: "Reference Drug", value: selectedCrl.reference_drug },
                      { label: "Signatory", value: selectedCrl.signatory_name },
                      { label: "Issuing Division", value: selectedCrl.issuing_division },
                    ].map(({ label, value }) => (
                      <Box key={label} display="flex" gap={1} mb={0.5}>
                        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160 }}>{label}:</Typography>
                        <Typography variant="body2">{value || "N/A"}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>

                {/* Indications */}
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight="bold" color="primary" mb={1}>Proposed Indications</Typography>
                  <Paper sx={{ p: 2 }}>
                    {(() => {
                      const validIndications = (selectedCrl.indications || []).filter(ind => ind != null && String(ind).trim() !== '');
                      return validIndications.length > 0 ? (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {validIndications.map((ind, i) => (
                            <Chip key={i} label={String(ind)} size="small" variant="outlined" color="primary" />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">No indications data available</Typography>
                      );
                    })()}
                  </Paper>
                </Grid>

                {/* Dosage Forms */}
                {(selectedCrl.dosage_forms || []).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="bold" color="primary" mb={1}>Dosage Forms</Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead sx={{ backgroundColor: "#e3f2fd" }}>
                          <TableRow>
                            <TableCell fontWeight="bold">Form</TableCell>
                            <TableCell fontWeight="bold">Strength</TableCell>
                            <TableCell fontWeight="bold">Route</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedCrl.dosage_forms.map((df, i) => (
                            <TableRow key={i}>
                              <TableCell>{df.form || "N/A"}</TableCell>
                              <TableCell>{df.strength || "N/A"}</TableCell>
                              <TableCell>{df.route || "N/A"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                {/* Deficiencies */}
                {(selectedCrl.deficiencies || []).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="bold" color="error" mb={1}>
                      <AssignmentLateIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                      Deficiencies ({selectedCrl.deficiency_count || 0})
                    </Typography>
                    {selectedCrl.deficiencies.map((def, i) => (
                      <Paper key={i} sx={{ p: 2, mb: 2, borderLeft: "4px solid #f44336" }}>
                        <Chip label={def.category || "General"} size="small" color="error" sx={{ mb: 1 }} />
                        <Box
                          sx={MESSAGE_STYLES}
                          dangerouslySetInnerHTML={{ __html: convertMessageToHtml(
                            (def.description || "").replace(/\. (\d{1,2})\. /g, '.\n$1. ')
                          ) }}
                        />
                        {def.required_action && (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" fontWeight="bold" color="primary">Required Action:</Typography>
                            <Typography variant="body2">{def.required_action}</Typography>
                          </>
                        )}
                      </Paper>
                    ))}
                  </Grid>
                )}

                {/* Required Actions */}
                {(selectedCrl.required_actions || []).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight="bold" color="primary" mb={1}>Required Actions / Post-Marketing Commitments</Typography>
                    <Paper sx={{ p: 2 }}>
                      {selectedCrl.required_actions.map((action, i) => (
                        <Box key={i} display="flex" gap={1} mb={1}>
                          <Typography variant="body2" fontWeight="bold">{i + 1}.</Typography>
                          <Typography variant="body2">{action}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setOpenDialog(false); setSelectedCrl(null); }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CRLSummary;
